/**
 * Claimflow — pure helpers (meldingen, foutclassificatie, verificatiestatussen).
 * Geeft nooit eigenaarsgegevens, tokens of technische details door.
 */

export const CLAIM_HAS_OWNER_MESSAGE =
  'Dit profiel heeft al een eigenaar. Neem contact op met de eigenaar om je toegang aan te passen.';

export type ClaimProfileState = 'member' | 'has_owner' | 'open' | 'not_authenticated' | 'unknown';

function rawOf(error: unknown): string {
  const e = (error ?? {}) as { message?: string; details?: string };
  return [e.message, e.details].filter(Boolean).join(' ');
}

export function claimSubmitErrorKind(error: unknown): 'has_owner' | 'member' | 'active_claim' | 'other' {
  const raw = rawOf(error);
  if (raw.includes('claim_profile_has_owner')) return 'has_owner';
  if (raw.includes('claim_already_member')) return 'member';
  if (raw.includes('claim_already_active')) return 'active_claim';
  return 'other';
}

const ADMIN_ERROR_LABELS: Record<string, string> = {
  claim_profile_has_owner: 'Dit profiel heeft al een actieve eigenaar. De claim is niet goedgekeurd.',
  claim_legacy_owner_conflict: 'Dit profiel is al aan een ander account gekoppeld. Controleer het profiel handmatig.',
  claim_not_verified: 'Deze claim is nog niet per e-mail bevestigd.',
  claim_state_conflict: 'De status van deze claim is inmiddels gewijzigd. Vernieuw de lijst.',
  claim_not_rejectable: 'Deze claim kan niet meer worden afgewezen.',
  claim_not_found: 'Deze claim bestaat niet meer. Vernieuw de lijst.',
  claim_admin_forbidden: 'Je hebt geen rechten voor deze actie.',
};

export function describeAdminClaimError(error: unknown): string {
  const raw = rawOf(error);
  for (const key of Object.keys(ADMIN_ERROR_LABELS)) {
    if (raw.includes(key)) return ADMIN_ERROR_LABELS[key];
  }
  return 'De actie kon niet worden uitgevoerd. Probeer het opnieuw.';
}

// ─── Verificatiepagina ─────────────────────────────────────────────────────
export const VERIFICATION_STATES = [
  'ready', 'verified', 'already_verified', 'expired', 'invalid', 'unavailable', 'has_owner',
] as const;
export type VerificationState = typeof VERIFICATION_STATES[number];

export function normalizeVerificationState(v: unknown): VerificationState | 'error' {
  return typeof v === 'string' && (VERIFICATION_STATES as readonly string[]).includes(v)
    ? (v as VerificationState)
    : 'error';
}

export const VERIFICATION_COPY: Record<VerificationState | 'error' | 'missing', { title: string; body: string }> = {
  ready: { title: 'Bevestig je e-mailadres', body: 'Klik op de knop om je e-mailadres te bevestigen. Daarna beoordeelt een beheerder je claim.' },
  verified: { title: 'E-mailadres bevestigd', body: 'Je e-mailadres is bevestigd. Je claim wordt nu beoordeeld door een beheerder.' },
  already_verified: { title: 'Al bevestigd', body: 'Dit e-mailadres is al bevestigd. Je claim wordt beoordeeld door een beheerder.' },
  expired: { title: 'Link verlopen', body: 'Deze bevestigingslink is verlopen. Neem contact met ons op voor een nieuwe link.' },
  invalid: { title: 'Link ongeldig', body: 'Deze bevestigingslink is niet geldig. Controleer of je de volledige link uit de e-mail hebt geopend.' },
  missing: { title: 'Link niet gevonden', body: 'Open de bevestigingslink opnieuw vanuit je e-mail.' },
  unavailable: { title: 'Claim niet meer beschikbaar', body: 'Deze claim is niet meer beschikbaar. Neem contact met ons op als je vragen hebt.' },
  has_owner: { title: 'Profiel heeft al een eigenaar', body: CLAIM_HAS_OWNER_MESSAGE },
  error: { title: 'Er ging iets mis', body: 'We konden je link nu niet controleren. Probeer het later opnieuw.' },
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isClaimToken = (v: unknown): v is string => typeof v === 'string' && UUID_RE.test(v);
